"use client";

import { useEffect, useRef } from "react";

const PALETTE = {
  violet: "#836FFF",
  indigo: "#211951",
  primary: "#2563eb",
  mint: "#15F5BA",
  green: "#10b981",
  soft: "#a5b4fc",
} as const;

type Recipe = {
  id: string;
  w: number;
  size: readonly [number, number];
  color: string | null;
  op: number;
};

const RECIPES: Recipe[] = [
  { id: "line", w: 4, size: [110, 200], color: PALETTE.primary, op: 0.09 },
  { id: "candles", w: 3, size: [70, 120], color: null, op: 0.28 },
  { id: "coin", w: 4, size: [44, 78], color: PALETTE.violet, op: 0.28 },
  { id: "pct", w: 3, size: [80, 130], color: PALETTE.green, op: 0.45 },
  { id: "bars", w: 4, size: [60, 110], color: PALETTE.indigo, op: 0.1 },
  { id: "tile", w: 2, size: [120, 180], color: PALETTE.soft, op: 0.5 },
  { id: "donut", w: 3, size: [50, 90], color: PALETTE.violet, op: 0.22 },
  { id: "piggy", w: 2, size: [72, 110], color: PALETTE.indigo, op: 0.12 },
  { id: "compound", w: 4, size: [140, 220], color: PALETTE.mint, op: 0.18 },
];

const POOL: Recipe[] = RECIPES.flatMap((r) => Array<Recipe>(r.w).fill(r));

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

type Mode = "up" | "side";

function makeSprite(recipe: Recipe, mode: Mode): HTMLDivElement {
  const [smin, smax] = recipe.size;
  const size = rand(smin, smax);

  const outer = document.createElement("div");
  outer.className = "ahb-sprite";

  const inner = document.createElement("div");

  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("width", String(size));
  svg.style.display = "block";
  svg.style.width = size + "px";
  svg.style.height = "auto";
  if (recipe.color) svg.style.color = recipe.color;

  const use = document.createElementNS(ns, "use");
  use.setAttribute("href", "#ahb-spr-" + recipe.id);
  svg.appendChild(use);

  const defSym = document.querySelector("#ahb-spr-" + recipe.id);
  const vbAttr = defSym?.getAttribute("viewBox");
  if (vbAttr) {
    const vb = vbAttr.split(/\s+/).map(Number);
    const ratio = vb[3] / vb[2];
    svg.setAttribute("viewBox", vb.join(" "));
    svg.style.height = size * ratio + "px";
  }

  inner.appendChild(svg);
  outer.appendChild(inner);

  outer.style.setProperty("--op", String(recipe.op));

  if (mode === "up") {
    const startX = rand(-5, 100);
    const swayX = rand(-12, 12);
    const r0 = rand(-6, 6);
    const r1 = r0 + rand(-10, 10);
    outer.style.left = startX + "vw";
    outer.style.top = "0";
    outer.style.setProperty("--x0", "0px");
    outer.style.setProperty("--x1", swayX + "vw");
    outer.style.setProperty("--r0", r0 + "deg");
    outer.style.setProperty("--r1", r1 + "deg");
    const dur = rand(38, 70);
    const delay = -rand(0, dur);
    outer.style.animation = `ahb-drift-up ${dur}s linear ${delay}s infinite`;
  } else {
    const startY = rand(5, 90);
    const sway = rand(-40, 40);
    const r0 = rand(-4, 4);
    const r1 = r0 + rand(-6, 6);
    outer.style.top = startY + "%";
    outer.style.left = "0";
    outer.style.setProperty("--y0", "0px");
    outer.style.setProperty("--y1", sway + "px");
    outer.style.setProperty("--r0", r0 + "deg");
    outer.style.setProperty("--r1", r1 + "deg");
    const dur = rand(55, 95);
    const delay = -rand(0, dur);
    outer.style.animation = `ahb-drift-side ${dur}s linear ${delay}s infinite`;
  }

  const bobDur = rand(4.5, 9);
  const bobDelay = -rand(0, bobDur);
  inner.style.animation = `ahb-float-bob ${bobDur}s ease-in-out ${bobDelay}s infinite`;
  inner.style.setProperty("--r", "0deg");

  return outer;
}

export default function ArticleHeroBackground() {
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // Scale density down on small screens so it doesn't feel busy.
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches;
    const COUNT_UP = isMobile ? 8 : 14;
    const COUNT_SIDE = isMobile ? 5 : 10;

    const sprites: HTMLDivElement[] = [];
    for (let i = 0; i < COUNT_UP; i++) {
      const s = makeSprite(pick(POOL), "up");
      stage.appendChild(s);
      sprites.push(s);
    }
    for (let i = 0; i < COUNT_SIDE; i++) {
      const s = makeSprite(pick(POOL), "side");
      stage.appendChild(s);
      sprites.push(s);
    }

    return () => {
      sprites.forEach((s) => s.remove());
    };
  }, []);

  return (
    <>
      {/* Hidden SVG sprite library — defined once, instances reference via <use href> */}
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          {/* (1) Line chart trending up */}
          <symbol id="ahb-spr-line" viewBox="0 0 120 60">
            <polyline
              points="4,52 22,40 36,46 52,28 70,34 86,18 104,22 116,8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="116" cy="8" r="3.2" fill="currentColor" />
            <line
              x1="4"
              y1="58"
              x2="116"
              y2="58"
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="2 4"
              opacity="0.45"
            />
          </symbol>

          {/* (2) Candlestick cluster */}
          <symbol id="ahb-spr-candles" viewBox="0 0 100 80">
            <g strokeWidth="1.6" strokeLinecap="round">
              <line x1="12" y1="14" x2="12" y2="62" stroke="#10b981" />
              <rect x="7" y="24" width="10" height="28" fill="#10b981" />
              <line x1="30" y1="22" x2="30" y2="72" stroke="#ef4444" />
              <rect x="25" y="32" width="10" height="28" fill="#ef4444" />
              <line x1="48" y1="8" x2="48" y2="58" stroke="#10b981" />
              <rect x="43" y="16" width="10" height="34" fill="#10b981" />
              <line x1="66" y1="6" x2="66" y2="48" stroke="#10b981" />
              <rect x="61" y="14" width="10" height="24" fill="#10b981" />
              <line x1="84" y1="2" x2="84" y2="44" stroke="#10b981" />
              <rect x="79" y="8" width="10" height="28" fill="#10b981" />
            </g>
          </symbol>

          {/* (3) Coin with kr */}
          <symbol id="ahb-spr-coin" viewBox="0 0 64 64">
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
            />
            <circle
              cx="32"
              cy="32"
              r="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              opacity="0.5"
            />
            <text
              x="32"
              y="40"
              textAnchor="middle"
              fontFamily="Open Sans, sans-serif"
              fontWeight="800"
              fontSize="20"
              fill="currentColor"
            >
              kr
            </text>
          </symbol>

          {/* (4) Percentage / arrow up badge */}
          <symbol id="ahb-spr-pct" viewBox="0 0 110 44">
            <rect
              x="1"
              y="1"
              width="108"
              height="42"
              rx="21"
              fill="rgba(255,255,255,0.82)"
              stroke="currentColor"
              strokeWidth="1.6"
            />
            <path d="M16 30 L24 18 L32 30 Z" fill="#10b981" />
            <text
              x="68"
              y="29"
              textAnchor="middle"
              fontFamily="Open Sans, sans-serif"
              fontWeight="700"
              fontSize="17"
              fill="#10b981"
            >
              +8,4%
            </text>
          </symbol>

          {/* (5) Bar chart ascending */}
          <symbol id="ahb-spr-bars" viewBox="0 0 90 70">
            <g fill="currentColor">
              <rect x="6" y="46" width="14" height="20" rx="2" opacity="0.55" />
              <rect x="26" y="34" width="14" height="32" rx="2" opacity="0.7" />
              <rect x="46" y="22" width="14" height="44" rx="2" opacity="0.85" />
              <rect x="66" y="8" width="14" height="58" rx="2" />
            </g>
            <line
              x1="2"
              y1="66"
              x2="88"
              y2="66"
              stroke="currentColor"
              strokeWidth="1.4"
            />
          </symbol>

          {/* (6) Sparkline tile (mini card) */}
          <symbol id="ahb-spr-tile" viewBox="0 0 140 80">
            <rect
              x="1"
              y="1"
              width="138"
              height="78"
              rx="10"
              fill="rgba(255,255,255,0.86)"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <text
              x="12"
              y="22"
              fontFamily="Open Sans, sans-serif"
              fontSize="11"
              fontWeight="600"
              fill="#6b7280"
            >
              OMXS30
            </text>
            <text
              x="12"
              y="42"
              fontFamily="Open Sans, sans-serif"
              fontSize="16"
              fontWeight="800"
              fill="#211951"
            >
              2 587,40
            </text>
            <text
              x="12"
              y="58"
              fontFamily="Open Sans, sans-serif"
              fontSize="11"
              fontWeight="700"
              fill="#10b981"
            >
              +1,82%
            </text>
            <polyline
              points="78,62 88,55 96,58 104,48 112,52 120,38 130,32"
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </symbol>

          {/* (7) Donut / portfolio slice */}
          <symbol id="ahb-spr-donut" viewBox="0 0 64 64">
            <circle
              cx="32"
              cy="32"
              r="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="10"
              opacity="0.25"
            />
            <circle
              cx="32"
              cy="32"
              r="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="10"
              strokeDasharray="90 138"
              strokeDashoffset="0"
              transform="rotate(-90 32 32)"
              strokeLinecap="butt"
            />
          </symbol>

          {/* (8) Piggy bank */}
          <symbol id="ahb-spr-piggy" viewBox="0 0 80 60">
            <g
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path
                d="M10 36 C10 22, 26 14, 42 14 C58 14, 70 22, 70 34
                   C70 42, 64 48, 58 50 L58 56 L52 56 L50 52
                   L26 52 L24 56 L18 56 L18 48
                   C12 46, 10 42, 10 36 Z"
              />
              <circle cx="64" cy="32" r="1.6" fill="currentColor" />
              <circle cx="54" cy="26" r="1.6" fill="currentColor" />
              <line x1="36" y1="18" x2="48" y2="18" strokeWidth="3" />
              <path d="M30 16 L28 8 L36 14" />
            </g>
          </symbol>

          {/* (9) Compounding curve */}
          <symbol id="ahb-spr-compound" viewBox="0 0 140 80">
            <defs>
              <linearGradient id="ahb-cg" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M4 74 C 30 72, 50 66, 70 54 C 90 42, 110 22, 136 6 L136 78 L4 78 Z"
              fill="url(#ahb-cg)"
            />
            <path
              d="M4 74 C 30 72, 50 66, 70 54 C 90 42, 110 22, 136 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
            <line
              x1="4"
              y1="78"
              x2="136"
              y2="78"
              stroke="currentColor"
              strokeWidth="1"
              opacity="0.4"
            />
          </symbol>
        </defs>
      </svg>

      {/* Stage — sprites are appended here in useEffect */}
      <div ref={stageRef} className="ahb-stage" aria-hidden="true" />
    </>
  );
}
